/**
 * A running (or just finished) search, kept compact: one progress line, the
 * overlap strip, the close-calls list, then the creators found so far in a
 * short scroll box. The raw log is folded away.
 */
import { useEffect, useRef, useState, type JSX } from 'react';
import { AlertCircle, Check, CheckCircle2, Loader2, Square, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { RangeSelectHint } from '@/components/ui/range-select';
import { applyToSet } from '@/hooks/useRangeSelection';
import { useStopScrape } from '../hooks/scrape';
import { useBulkDeleteLeads } from '../hooks/leads';
import { SkipSummary } from '../components/OutreachStatus';
import type { NearMissOption, SearchSchema } from '../api/types';
import type { SearchJobState } from './hooks';
import { nearMissesFromWiden } from './insights';
import { LiveResults } from './LiveResults';
import { NearMissPanel } from './NearMissPanel';
import { OverlapPanel } from './OverlapPanel';
import { SoundText } from '../components/SoundLink';

function lastDoneMessage(state: SearchJobState): string | null {
  if (state.status !== 'done' && state.status !== 'error') return null;
  for (let i = state.logs.length - 1; i >= 0; i--) {
    const log = state.logs[i];
    if (log && (log.type === 'done' || log.type === 'error')) return log.msg;
  }
  return null;
}

export function JobPanel({
  state,
  jobId,
  folderPublicId,
  folderName,
  schema,
  ranWith,
  onApplyNearMiss,
  onSearchAgain,
  onDone,
}: {
  state: SearchJobState;
  jobId: string | null;
  folderPublicId: string;
  folderName?: string | null;
  schema: SearchSchema | null;
  /** The params this job ran with, if known — "from" values for the legacy widen output. */
  ranWith: Record<string, unknown> | null;
  onApplyNearMiss?: (o: NearMissOption) => void;
  onSearchAgain: () => void;
  onDone: () => void;
}): JSX.Element {
  const stop = useStopScrape();
  const stopping = !!jobId && stop.variables === jobId && (stop.isPending || stop.isSuccess);
  const bulkDelete = useBulkDeleteLeads(folderPublicId);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [showLog, setShowLog] = useState(false);
  const logEnd = useRef<HTMLDivElement>(null);

  const running = state.status === 'running';
  const done = state.status === 'done' || state.status === 'error';
  const doneMsg = lastDoneMessage(state);

  useEffect(() => {
    setSelected(new Set());
  }, [jobId]);
  useEffect(() => {
    if (running && showLog) logEnd.current?.scrollIntoView({ block: 'nearest' });
  }, [state.logs.length, running, showLog]);

  const nearMisses = state.nearMisses ?? nearMissesFromWiden(state.widen, ranWith);
  const canInclude = state.nearMisses !== null;

  const stats = [
    `${state.saved.toLocaleString()} found`,
    `${state.checked.toLocaleString()} checked`,
    state.skipped > 0 && `${state.skipped.toLocaleString()} skipped`,
    state.preFiltered > 0 && `${state.preFiltered.toLocaleString()} pre-filtered`,
    `${state.apiCalls.toLocaleString()} API calls`,
    state.cacheHits > 0 && `${state.cacheHits.toLocaleString()} cache hits`,
  ].filter(Boolean);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <div className="flex min-w-[14rem] flex-1 items-start gap-2">
          {running ? (
            <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-brand" strokeWidth={2} />
          ) : state.status === 'error' ? (
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive-strong" strokeWidth={2} />
          ) : (
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success-strong" strokeWidth={2} />
          )}
          <p className="min-w-0 text-sm" aria-live="polite">
            <span className="font-semibold">
              {running ? 'Searching…' : state.status === 'error' ? 'Search failed' : 'Search complete'}
            </span>
            <span className="text-muted-foreground"> · {stats.join(' · ')}</span>
          </p>
        </div>
        <div className="ml-auto flex shrink-0 items-center gap-2">
          {running && (
            // Stop is a request: calls already in flight finish first.
            <Button type="button" variant="outline" size="sm" disabled={stopping || !jobId} onClick={() => jobId && stop.mutate(jobId)}>
              {stopping ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Square className="h-3 w-3 fill-current" />}
              {stopping ? 'Stopping…' : 'Stop'}
            </Button>
          )}
          {done && selected.size > 0 && <RangeSelectHint className="hidden sm:inline" />}
          {done && selected.size > 0 && (
            <ConfirmDialog
              trigger={
                <Button type="button" variant="destructive" size="sm">
                  <Trash2 className="h-3.5 w-3.5" />
                  Remove {selected.size}
                </Button>
              }
              title={`Remove ${selected.size} creator${selected.size === 1 ? '' : 's'} from this folder?`}
              description="They were saved by this search. Removing them takes them out of the folder; it doesn't suppress them."
              tone="destructive"
              confirmLabel="Remove"
              pending={bulkDelete.isPending}
              onConfirm={() =>
                bulkDelete.mutate([...selected], {
                  onSuccess: () => setSelected(new Set()),
                })
              }
            />
          )}
          {done && (
            <>
              <Button type="button" variant="outline" size="sm" onClick={onSearchAgain}>
                Search again
              </Button>
              <Button type="button" size="sm" onClick={onDone}>
                <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
                Done
              </Button>
            </>
          )}
        </div>
      </div>

      {running && state.stage && <p className="truncate text-xs text-muted-foreground">Now: <SoundText text={state.stage} /></p>}

      <SkipSummary skipped={state.skipped} breakdown={state.skipBreakdown} />
      <OverlapPanel overlap={state.overlap} jobId={jobId} folderPublicId={folderPublicId} folderName={folderName} />
      <NearMissPanel
        nearMisses={nearMisses}
        schema={schema}
        jobId={jobId}
        folderPublicId={folderPublicId}
        canInclude={canInclude}
        onApply={onApplyNearMiss}
      />

      {state.leads.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-sm">
            <span className="font-semibold">Creators found</span>{' '}
            <span className="text-muted-foreground">
              ({state.leads.length.toLocaleString()}){done && ' · tick any you don’t want kept'}
            </span>
          </p>
          <LiveResults
            leads={state.leads}
            selectable={done}
            selected={selected}
            onApply={applyToSet(setSelected)}
          />
        </div>
      )}

      {doneMsg && (
        <p className={state.status === 'error' ? 'text-sm text-destructive-strong' : 'text-sm text-muted-foreground'}>{doneMsg}</p>
      )}
      {state.status === 'error' && state.error && <p className="text-sm text-destructive-strong">{state.error}</p>}

      <div>
        <Button type="button" variant="link" size="sm" className="h-auto px-0 text-muted-foreground" onClick={() => setShowLog((v) => !v)}>
          {showLog ? 'Hide log' : `Show log (${state.logs.length.toLocaleString()} lines)`}
        </Button>
        {showLog && (
          <div className="dx-log-stream mt-1.5">
            {state.logs.map((log, i) => (
              <div key={i} className={`dx-log-line is-${log.type}`}>
                <span className="dx-log-time">{new Date(log.ts).toLocaleTimeString()}</span>
                <span className="dx-log-msg">
                  <SoundText text={log.msg} />
                </span>
              </div>
            ))}
            {state.logs.length === 0 && <div className="dx-log-line">Waiting for the first log line…</div>}
            <div ref={logEnd} />
          </div>
        )}
      </div>
    </div>
  );
}
